// Copyright © 2017-2019  SDG Systems, LLC. All rights reserved.
// This file may not be used in any form without the express written permission of SDG Systems, LLC

<template>
  <v-container fluid grid-list-md>
    <v-card>
      <v-toolbar flat>
        <v-btn icon @click="exit"><v-icon>clear</v-icon></v-btn>
        <v-toolbar-title v-if="newWebhook">Add Webhook</v-toolbar-title>
        <v-toolbar-title v-else>Edit Webhook</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn class="mx-1" v-if="canModify" dark color="blue" @click="saveWebhook">Save</v-btn>
        <v-btn class="mx-1" v-if="canModify && !newWebhook" dark color="red" @click="showDeleteDialog = true">Delete</v-btn>
      </v-toolbar>
      <v-card-text>
        <v-form v-model="formValid" ref="form">
          <v-row wrap>
            <v-col cols="12" md="6">
              <v-text-field
                name="input"
                label="Target URL"
                v-model="webhook.targetUrl"
                :rules="[ruleRequired]"
                :disabled="!canModify"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-col cols="12" md="12">
            <v-row v-for="(tempHeader, i) in tempHeaders" v-bind:key="i" xs="12" md="12">
              <v-col cols="6" md="6">
                <v-text-field
                  name="header"
                  label="Custom Header"
                  v-model="tempHeaders[i].name"
                  :disabled="!canModify"
                ></v-text-field>
              </v-col>
              <v-col cols="6" md="6">
                <v-text-field
                  name="value"
                  label="Header Value"
                  v-model="tempHeaders[i].value"
                  :disabled="!canModify"
                  append-outer-icon="delete"
                  @click:append-outer="removeHeader(i)"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-btn text @click="addHeader()">
                Add Header
            </v-btn>
          </v-col>
          <v-col cols="12" md="12">
            <webhook-field-edit v-for="(field, i) in webhook.fields" v-bind:key="i" :field="field" v-on:delete-field="removeField(i)" />
          </v-col>
          <v-col cols="12" md="12">
            <v-btn text @click="addField()">
                Add Field
            </v-btn>
          </v-col>
        </v-form>
      </v-card-text>
    </v-card>

    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title>
          <div class="headline">Confirm Delete</div>
        </v-card-title>
        <v-card-text>Are you sure you want to delete this webhook?  <b>This action cannot be undone.</b></v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click.native="showDeleteDialog = false">Cancel</v-btn>
          <v-btn text color="red" @click="deleteWebhook()">I'm Sure</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <error-snackbar v-model="errorText"></error-snackbar>
  </v-container>
</template>

<script>
  import { mapGetters } from 'vuex'
  import ApiRequests from '@/api/ApiRequests'
  import {PERMISSIONS} from '@sdgsystems/vue-common/components/constants'
  import {ENDPOINT} from '@/shared/constants'
  import {checkPermission} from '@sdgsystems/vue-common/components/user'
  // import {AXIOSAPI} from '@sdgsystems/vue-common/components/requests-common'
  import ErrorSnackbar from '@sdgsystems/vue-common/components/ErrorSnackbar'
  import Rules from '@sdgsystems/vue-common/components/rules'
  import WebhookFieldEdit from '@/components/WebhookFieldEdit'

  export default {
    components: {
      ErrorSnackbar,
      WebhookFieldEdit
    },

    data () {
      return {
        endpoint: ENDPOINT,
        errorText: '',
        formValid: null,
        showDeleteDialog: false,
        webhook: {
          event: 'photoUpload',
          format: 'json',
          targetUrl: '',
          requestHeaders: {},
          fields: []
        },
        events: [
          {
            text: 'Photo Upload',
            value: 'photoUpload'
          },
          {
            text: 'Photo Update',
            value: 'photoUpdate'
          },
          {
            text: 'First Update Only',
            value: 'photoFirstUpdate'
          }
        ],
        tempHeaders: []
      }
    },

    created () {
      this.getWebhook()
    },

    computed: {
      ...mapGetters({
        activeAccessRules: 'accessRules',
        activeUser: 'user',
        isSuperAdmin: 'isSuperAdmin'
      }),

      canModify: function () {
        return checkPermission(this.activeAccessRules, 'webhooks', PERMISSIONS.MODIFY)
      },

      newWebhook: function () {
        return (this.$route.params.id === 'new')
      }
    },

    methods: {
      addHeader: function () {
        this.tempHeaders.push({
          'name': '',
          'value': ''
        })
      },

      removeHeader: function (i) {
        this.tempHeaders.splice(i, 1)
      },

      addField: function () {
        this.webhook.fields.push({})
      },

      removeField: function (i) {
        this.webhook.fields.splice(i, 1)
      },

      deleteWebhook: function () {
        ApiRequests.deleteWebhook(this.user.id).then(r => {
          this.exit()
        }).catch(e => {
          this.errorText = e.response.statusText + ': ' + e.response.data
        })
      },

      getWebhook: function () {
        if (this.newWebhook) {
        } else {
          ApiRequests.getWebhook(this.$route.params.id).then(r => {
            this.webhook = r.data
            for (let header in this.webhook.requestHeaders) {
              if (this.webhook.requestHeaders.hasOwnProperty(header)) {
                this.tempHeaders.push({
                  'name': header,
                  'value': this.webhook.requestHeaders[header]
                })
              }
            }
          }).catch(e => {
            this.errorText = e.response.statusText + ': ' + e.response.data
          })
        }
      },

      saveWebhook: function () {
        if (this.$refs.form.validate()) {
          this.webhook.requestHeaders = {}
          for (const header of this.tempHeaders) {
            this.webhook.requestHeaders[header.name] = header.value
          }
          if (this.newWebhook) {
            ApiRequests.postWebhook(this.webhook).then(response => {
              this.exit()
            }).catch(e => {
              console.log(e)
              this.errorText = e.response.statusText + ': ' + e.response.data
            })
          } else {
            ApiRequests.putWebhook(this.webhook).then(response => {
              this.exit()
            }).catch(e => {
              console.log(e)
              this.errorText = e.response.statusText + ': ' + e.response.data
            })
          }
        } else {
          this.errorText = 'Please fill out all required fields'
        }
      },

      ruleOnePlus: function (x) {
        if (this.ruleRequired(x)) {
          if (x.length > 0) {
            return true
          } else {
            return 'You must select at least one item'
          }
        }
        return this.ruleRequired(x)
      },

      ruleRequired: function (x) {
        return Rules.required(x)
      },

      rulePassword: function (password) {
        return Rules.password(password)
      },

      exit: function () {
        this.$router.push({name: 'webhooks'})
      }
    }
  }
</script>



// WEBPACK FOOTER //
// WebhookEdit.vue?8fbe4630